// SignUpPage.js

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TextField } from '@mui/material';
import './SignUpPage.css';
import { auth } from '../utils/firebaseConfig';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { saveDataToDB, getUserByUsername } from '../utils/dbUtilities';
import { useAuth } from '../contexts/AuthContext';

const SignUpPage = () => {
  const [firstName, setFirstName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mathChallenge, setMathChallenge] = useState({});
  const [mathAnswer, setMathAnswer] = useState('');
  const [error, setError] = useState(null);

  const { setCurrentUser, setIsLoggedIn, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    generateMathChallenge();

    const handleFocus = (event) => {
      console.log("Input focused", event.target);
    };

    const handleBlur = (event) => {
      console.log("Input blurred", event.target);
    };

    const inputs = document.querySelectorAll('.signup-input');
    inputs.forEach(input => {
      input.addEventListener('focus', handleFocus);
      input.addEventListener('blur', handleBlur);
    });

    return () => {
      inputs.forEach(input => {
        input.removeEventListener('focus', handleFocus);
        input.removeEventListener('blur', handleBlur);
      });
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, navigate]);

  const generateMathChallenge = () => {
    const num1 = Math.floor(Math.random() * 10);
    const num2 = Math.floor(Math.random() * 10);
    setMathChallenge({ num1, num2 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('handleSubmit called with:', { firstName, username, email, password, mathAnswer });

    if (parseInt(mathAnswer) !== mathChallenge.num1 + mathChallenge.num2) {
      console.log('Math challenge failed.');
      setError('Incorrect answer to math problem.');
      generateMathChallenge();
      return;
    }

    try {
      console.log(`Checking if username "${username}" exists...`);
      const usernameExists = await getUserByUsername(username.toLowerCase());
      if (usernameExists) {
        console.log('Username check failed: Username exists.');
        setError("Username already taken. Try again.");
        return;
      }
    } catch (error) {
      console.error('Error checking username:', error);
      setError("Failed to check username availability.");
      return;
    }

    try {
      console.log('Creating user with email and password...');
      const userCredential = await createUserWithEmailAndPassword(auth, email.toLowerCase(), password);
      const firebaseUser = userCredential.user;
      console.log('User created:', firebaseUser);

      if (firebaseUser) {
        await updateProfile(firebaseUser, { displayName: username.toLowerCase() });
        await firebaseUser.reload();
        console.log('Firebase user profile updated.');

        const accountCreationTimestamp = Date.now();

        await saveDataToDB('users', {
          username: username.toLowerCase(),
          email: email.toLowerCase(),
          firstName: firstName,
          creationTime: accountCreationTimestamp,
          uid: firebaseUser.uid,
          blockedUsers: {}
        }, firebaseUser.uid);
        console.log(`SignUpPage: User data saved to database for ${firebaseUser.uid}`);

        setCurrentUser({ ...firebaseUser, displayName: username.toLowerCase() });
        setIsLoggedIn(true);
      }
    } catch (err) {
      console.error('Error during user creation:', err);
      if (err.code === "auth/email-already-in-use") {
        setError("Email already registered. Try logging in.");
      } else if (err.code === "auth/weak-password") {
        setError("Password should be at least 6 characters.");
      } else {
        setError(err.message);
      }
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-header">Sign Up</div>
      {error && <p className="error-message">{error}</p>}
      <input
        type="text"
        placeholder="First Name"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        className="signup-input"
      />
      <input
        type="text"
        placeholder="Username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        className="signup-input"
      />
      <input
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="signup-input"
      />
      <input
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="signup-input"
      />
      <div className="math-captcha">
        Solve this to prove you're not a robot: {mathChallenge.num1} + {mathChallenge.num2} = ?
      </div>
      <TextField
        fullWidth
        variant="outlined"
        type="text"
        value={mathAnswer}
        onChange={(e) => setMathAnswer(e.target.value)}
        error={error !== null && error.includes('math problem')}
        helperText={error && error.includes('math problem') ? error : ''}
        sx={{ '& .MuiInputBase-input': { textAlign: 'center' } }}
        margin="normal"
        className="signup-input"
      />
      <button className="signup-button" onClick={handleSubmit}>Sign Up</button>
      <div className="signup-links">
        <div className="signup-text">
          Have an account? <Link to="/login">Login</Link>
        </div>
      </div>
    </div>
  );  
};

export default SignUpPage;
